import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Grid,
} from '@mui/material';
import FilterChips from '../../../common/filter-chips/filter-chips';
import CriticalOrderHowItWorks from '../../../common/critical-order-how-it-works/critical-order-how-it-works';
import axios from 'axios';
import React, { useContext, useState } from 'react';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { OrganisationSettings } from '../../../../interfaces/delivery-company/orders';

interface CriticalOrderCreditConfigProps {
  settings: OrganisationSettings;
}

function CriticalOrderCreditConfig({ settings }: CriticalOrderCreditConfigProps) {
  const { config } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [surchargeMethod, setSurchargeMethod] = useState<'FIXED_SURCHARGE' | 'FIXED_FACTOR'>(
    settings.critical_order_surcharge_method || 'FIXED_SURCHARGE'
  );
  const [fixedAmount, setFixedAmount] = useState<string>(
    settings.critical_order_surcharge_fixed_amount && settings.critical_order_surcharge_fixed_amount.length > 0
      ? String(settings.critical_order_surcharge_fixed_amount[0][1])
      : ''
  );
  const [factor, setFactor] = useState<string>(
    settings.critical_order_surcharge_factor || ''
  );
  const [defaultCredit, setDefaultCredit] = useState<string>(
    settings.default_merchant_critical_order_monthly_credit?.toString() || ''
  );

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    defaultCredit?: string;
    fixedAmount?: string;
    factor?: string;
  }>({});

  const validateForm = () => {
    const errors: { defaultCredit?: string; fixedAmount?: string; factor?: string } = {};

    // Validate default credit
    if (!defaultCredit || defaultCredit.trim() === '') {
      errors.defaultCredit = 'Default monthly credit is required';
    } else {
      const creditNum = parseInt(defaultCredit);
      if (isNaN(creditNum) || creditNum < 0) {
        errors.defaultCredit = 'Default monthly credit must be a non-negative number';
      }
    }

    // Validate surcharge method specific fields
    if (surchargeMethod === 'FIXED_SURCHARGE') {
      if (!fixedAmount || fixedAmount.trim() === '') {
        errors.fixedAmount = 'Fixed surcharge amount is required';
      } else {
        const amountNum = parseFloat(fixedAmount);
        if (isNaN(amountNum) || amountNum < 0) {
          errors.fixedAmount = 'Fixed surcharge amount must be a non-negative number';
        }
      }
    } else if (surchargeMethod === 'FIXED_FACTOR') {
      if (!factor || factor.trim() === '') {
        errors.factor = 'Multiplier factor is required';
      } else {
        const factorNum = parseFloat(factor);
        if (isNaN(factorNum) || factorNum < 1) {
          errors.factor = 'Multiplier factor must be a number greater than or equal to 1';
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const payload: any = {
        critical_order_surcharge_method: surchargeMethod,
        default_merchant_critical_order_monthly_credit: parseInt(defaultCredit),
      };

      if (surchargeMethod === 'FIXED_SURCHARGE') {
        payload.critical_order_surcharge_fixed_amount = [['USD', fixedAmount]];
      } else if (surchargeMethod === 'FIXED_FACTOR') {
        payload.critical_order_surcharge_factor = factor;
      }

      await axios.post(
        `${API_ENDPOINT}/api/v1/settings`,
        payload,
        config
      );
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setError('Failed to save settings: ' + e.message);
      } else {
        setError('Failed to save settings');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Configure monthly critical order credits for merchants and surcharges when they exceed their limits.
        These settings can be overridden per merchant.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        {/* Left side - Form inputs */}
        <Grid item xs={12} md={8}>
          <Box>
            <TextField
              fullWidth
              type="number"
              label="Default Monthly Credit per Merchant"
              value={defaultCredit}
              onChange={(e) => {
                setDefaultCredit(e.target.value);
                if (validationErrors.defaultCredit) {
                  setValidationErrors(prev => ({ ...prev, defaultCredit: undefined }));
                }
              }}
              error={!!validationErrors.defaultCredit}
              helperText={validationErrors.defaultCredit || "Number of critical orders each merchant can place per month before surcharges apply"}
              sx={{ mb: 2 }}
              inputProps={{
                min: 0,
                onWheel: (e: any) => e.target.blur() // Prevent wheel scroll
              }}
            />

            <Box sx={{ mb: 2}}>
              <FilterChips
                label="Surcharge Method"
                options={[
                  { key: 'FIXED_SURCHARGE', label: 'Fixed Surcharge' },
                  { key: 'FIXED_FACTOR', label: 'Fixed Factor (Multiplier)' }
                ]}
                selected={[{ key: surchargeMethod, label: surchargeMethod === 'FIXED_SURCHARGE' ? 'Fixed Surcharge' : 'Fixed Factor (Multiplier)' }]}
                onChange={(selected) => {
                  if (selected.length > 0) {
                    setSurchargeMethod(selected[0].key as 'FIXED_SURCHARGE' | 'FIXED_FACTOR');
                  }
                }}
                getOptionLabel={(option) => option.label}
                optionsEqual={(a, b) => a.key === b.key}
              />
            </Box>

            {surchargeMethod === 'FIXED_SURCHARGE' && (
              <TextField
                fullWidth
                type="number"
                label="Fixed Surcharge Amount (USD)"
                value={fixedAmount}
                onChange={(e) => {
                  setFixedAmount(e.target.value);
                  if (validationErrors.fixedAmount) {
                    setValidationErrors(prev => ({ ...prev, fixedAmount: undefined }));
                  }
                }}
                error={!!validationErrors.fixedAmount}
                helperText={validationErrors.fixedAmount || "Extra charge added to delivery fee when merchant exceeds credit limit"}
                sx={{ mb: 2 }}
                inputProps={{
                  min: 0,
                  step: '0.01',
                  onWheel: (e: any) => e.target.blur() // Prevent wheel scroll
                }}
              />
            )}

            {surchargeMethod === 'FIXED_FACTOR' && (
              <TextField
                fullWidth
                required
                type="number"
                label="Multiplier Factor"
                value={factor}
                onChange={(e) => {
                  setFactor(e.target.value);
                  if (validationErrors.factor) {
                    setValidationErrors(prev => ({ ...prev, factor: undefined }));
                  }
                }}
                error={!!validationErrors.factor}
                helperText={validationErrors.factor || "Multiplier for base delivery fee (e.g., 2.0 = doubles the delivery charge, 1.5 = 50% increase)"}
                inputProps={{ 
                  step: '0.1', 
                  min: '1.0',
                  onWheel: (e: any) => e.target.blur() // Prevent wheel scroll
                }}
                sx={{ mb: 2 }}
              />
            )}
          </Box>
        </Grid>

        {/* Right side - How it works explanation */}
        <Grid item xs={12} md={4}>
          <CriticalOrderHowItWorks
            monthlyCredit={defaultCredit || '0'}
            surchargeMethod={surchargeMethod}
            fixedAmount={fixedAmount || '0'}
            factor={factor || '1.0'}
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Box width="100%" textAlign="center">
        {loading ? (
          <CircularProgress size={32} />
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
          >
            Save
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default CriticalOrderCreditConfig;

